/* eslint-disable react-refresh/only-export-components */
import type { Session, User } from "@supabase/supabase-js";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  getSession,
  isAdmin as checkIsAdmin,
  onAuthStateChange,
  signIn as authSignIn,
  signOut as authSignOut,
} from "@/lib/auth";
import { SessionTimeoutDialog } from "@/components/auth/SessionTimeoutDialog";
import { supabase } from "@/lib/supabase";
import {
  createTrustedDevice,
  prepareTotpChallenge,
  validateTrustedDevice,
} from "@/lib/adminMfaAccess";
import {
  clearTrustedDeviceToken,
  generateTrustedDeviceToken,
  getTrustedDeviceFingerprint,
  getTrustedDeviceToken,
  setTrustedDeviceToken,
} from "@/lib/trustedDevice";
import { shouldRequireTotp } from "@/lib/adminMfaDecision";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_WARNING_MS = 5 * 60 * 1000;
const MFA_FEATURE_ENABLED = (import.meta.env.VITE_ADMIN_MFA_TRUSTED_DEVICE_ENABLED ?? "true") !== "false";

interface SignInResult {
  error?: string;
  requiresTotp?: boolean;
}

interface PendingTotpChallenge {
  factorId: string;
  challengeId: string;
  rememberDevice: boolean;
  expiresAt: number | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  requiresTotp: boolean;
  mfaFeatureEnabled: boolean;
  accessDeniedMessage: string | null;
  signIn: (email: string, password: string, options?: { rememberDevice?: boolean }) => Promise<SignInResult>;
  verifyTotp: (code: string) => Promise<SignInResult>;
  cancelTotpChallenge: () => Promise<void>;
  signOut: () => Promise<void>;
  clearAccessDeniedMessage: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [pendingTotpChallenge, setPendingTotpChallenge] = useState<PendingTotpChallenge | null>(null);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string | null>(null);
  const [warningSeconds, setWarningSeconds] = useState<number | null>(null);

  const lastActivityRef = useRef<number>(Date.now());
  const signingOutRef = useRef(false);
  const authTransitionRef = useRef(false);

  const clearAccessDeniedMessage = useCallback(() => {
    setAccessDeniedMessage(null);
  }, []);

  const signOut = useCallback(async () => {
    signingOutRef.current = true;
    setPendingTotpChallenge(null);
    setRequiresTotp(false);
    setSession(null);
    setUser(null);
    setIsAdmin(false);
    setWarningSeconds(null);
    clearTrustedDeviceToken();
    await authSignOut();
    signingOutRef.current = false;
  }, []);

  const verifyAdmin = useCallback(async (userId: string): Promise<boolean> => {
    try {
      return await checkIsAdmin(userId);
    } catch {
      return false;
    }
  }, []);

  const resolveMfaStepAfterPassword = useCallback(
    async (rememberDevice: boolean): Promise<SignInResult> => {
      if (!MFA_FEATURE_ENABLED) {
        setPendingTotpChallenge(null);
        setRequiresTotp(false);
        return {};
      }

      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) {
        return {
          error: "Nao foi possivel validar seu MFA agora. Tente novamente em instantes.",
        };
      }

      const verifiedTotpFactors = Array.isArray(factorsData?.totp) ? factorsData.totp : [];
      if (verifiedTotpFactors.length === 0) {
        setPendingTotpChallenge(null);
        setRequiresTotp(false);
        return {};
      }

      const trustedToken = getTrustedDeviceToken();
      let trustedDeviceValid = false;
      if (trustedToken) {
        try {
          const trustedValidation = await validateTrustedDevice({
            token: trustedToken,
            fingerprint: getTrustedDeviceFingerprint(),
          });
          trustedDeviceValid = trustedValidation.valid;
        } catch {
          // fallback below to require TOTP challenge
        }

        if (!trustedDeviceValid) {
          clearTrustedDeviceToken();
        }
      }

      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const currentLevel = !aalError && (aalData?.currentLevel === "aal1" || aalData?.currentLevel === "aal2")
        ? aalData.currentLevel
        : null;
      const requiresTotpNow = shouldRequireTotp({
        mfaFeatureEnabled: MFA_FEATURE_ENABLED,
        hasVerifiedTotp: verifiedTotpFactors.length > 0,
        trustedDeviceValid,
        currentLevel,
      });

      if (!requiresTotpNow) {
        setPendingTotpChallenge(null);
        setRequiresTotp(false);
        return {};
      }

      const factorId = typeof verifiedTotpFactors[0]?.id === "string"
        ? verifiedTotpFactors[0].id
        : null;
      if (!factorId) {
        return { error: "Nao foi possivel iniciar o desafio TOTP." };
      }

      try {
        const challenge = await prepareTotpChallenge({ factorId });
        setPendingTotpChallenge({
          factorId: challenge.factorId,
          challengeId: challenge.challengeId,
          rememberDevice,
          expiresAt: challenge.expiresAt,
        });
        setRequiresTotp(true);
        return { requiresTotp: true };
      } catch (error) {
        return {
          error: error instanceof Error
            ? error.message
            : "Nao foi possivel iniciar o desafio TOTP.",
        };
      }
    },
    [],
  );

  useEffect(() => {
    let mounted = true;

    async function bootstrapAuth() {
      try {
        const currentSession = await getSession();
        if (!mounted) return;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          const admin = await verifyAdmin(currentSession.user.id);
          if (!mounted) return;

          if (!admin) {
            setAccessDeniedMessage("Acesso restrito a administradores.");
            await signOut();
            return;
          }

          const mfaResolution = await resolveMfaStepAfterPassword(false);
          if (!mounted) return;

          if (mfaResolution.error) {
            setAccessDeniedMessage(mfaResolution.error);
            await signOut();
            return;
          }

          if (mfaResolution.requiresTotp) {
            setIsAdmin(false);
            return;
          }

          setIsAdmin(true);
          lastActivityRef.current = Date.now();
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    bootstrapAuth().catch(() => {
      if (mounted) setIsLoading(false);
    });

    const subscription = onAuthStateChange(async (nextSession, event) => {
      if (!mounted || signingOutRef.current) return;
      if (authTransitionRef.current) return;

      // Invited user clicking email link — redirect to set password
      if (event === "PASSWORD_RECOVERY" && nextSession?.user) {
        setSession(nextSession);
        setUser(nextSession.user);
        setIsAdmin(false);
        setRequiresTotp(false);
        window.location.replace("/set-password");
        return;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setWarningSeconds(null);

      if (!nextSession?.user) {
        setIsAdmin(false);
        setRequiresTotp(false);
        setPendingTotpChallenge(null);
        return;
      }

      const admin = await verifyAdmin(nextSession.user.id);
      if (!mounted || signingOutRef.current) return;

      if (!admin) {
        setAccessDeniedMessage("Acesso restrito a administradores.");
        await signOut();
        return;
      }

      const mfaResolution = await resolveMfaStepAfterPassword(false);
      if (!mounted || signingOutRef.current) return;

      if (mfaResolution.error) {
        setAccessDeniedMessage(mfaResolution.error);
        await signOut();
        return;
      }

      if (mfaResolution.requiresTotp) {
        setIsAdmin(false);
        return;
      }

      setIsAdmin(true);
      lastActivityRef.current = Date.now();
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [resolveMfaStepAfterPassword, signOut, verifyAdmin]);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      options?: { rememberDevice?: boolean },
    ): Promise<SignInResult> => {
      authTransitionRef.current = true;
      try {
        const { data, error } = await authSignIn(email, password);
        if (error) {
          return { error: error.message || "Credenciais invalidas." };
        }

        if (!data.user) {
          return { error: "Sessao invalida apos login." };
        }

        setSession(data.session ?? null);
        setUser(data.user);

        const admin = await verifyAdmin(data.user.id);
        if (!admin) {
          await signOut();
          setAccessDeniedMessage("Acesso negado: usuario sem role admin.");
          return { error: "Acesso restrito a administradores." };
        }

        const mfaResolution = await resolveMfaStepAfterPassword(options?.rememberDevice === true);
        if (mfaResolution.error) {
          await signOut();
          return { error: mfaResolution.error };
        }
        if (mfaResolution.requiresTotp) {
          setIsAdmin(false);
          setAccessDeniedMessage(null);
          return { requiresTotp: true };
        }

        setRequiresTotp(false);
        setPendingTotpChallenge(null);
        setIsAdmin(true);
        lastActivityRef.current = Date.now();
        setAccessDeniedMessage(null);
        return {};
      } finally {
        authTransitionRef.current = false;
      }
    },
    [resolveMfaStepAfterPassword, signOut, verifyAdmin],
  );

  const verifyTotp = useCallback(
    async (code: string): Promise<SignInResult> => {
      if (!pendingTotpChallenge) {
        return { error: "Nenhum desafio MFA pendente." };
      }

      const sanitizedCode = code.trim();
      if (!/^\d{6}$/.test(sanitizedCode)) {
        return { error: "Informe um codigo TOTP valido com 6 digitos." };
      }

      const { error } = await supabase.auth.mfa.verify({
        factorId: pendingTotpChallenge.factorId,
        challengeId: pendingTotpChallenge.challengeId,
        code: sanitizedCode,
      });

      if (error) {
        return {
          error: "Codigo TOTP invalido ou expirado. Tente novamente.",
        };
      }

      if (pendingTotpChallenge.rememberDevice) {
        try {
          const trustedToken = generateTrustedDeviceToken();
          await createTrustedDevice({
            token: trustedToken,
            fingerprint: getTrustedDeviceFingerprint(),
            label: `${navigator.platform ?? "unknown"} - ${navigator.userAgent.slice(0, 60)}`,
          });
          setTrustedDeviceToken(trustedToken);
        } catch (trustedError) {
          console.warn("Falha ao registrar trusted device:", trustedError);
        }
      }

      setPendingTotpChallenge(null);
      setRequiresTotp(false);
      setIsAdmin(true);
      setAccessDeniedMessage(null);
      lastActivityRef.current = Date.now();
      return {};
    },
    [pendingTotpChallenge],
  );

  const cancelTotpChallenge = useCallback(async () => {
    await signOut();
  }, [signOut]);

  useEffect(() => {
    if (!session || !isAdmin) return;

    lastActivityRef.current = Date.now();

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      setWarningSeconds(null);
    };

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "pointerdown",
    ];

    events.forEach((eventName) => window.addEventListener(eventName, updateActivity, { passive: true }));

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = SESSION_TIMEOUT_MS - elapsed;

      if (remaining <= 0) {
        signOut().catch(() => undefined);
        return;
      }

      if (remaining <= SESSION_WARNING_MS) {
        setWarningSeconds(Math.ceil(remaining / 1000));
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
      events.forEach((eventName) => window.removeEventListener(eventName, updateActivity));
    };
  }, [isAdmin, session, signOut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      isLoading,
      isAdmin,
      requiresTotp,
      mfaFeatureEnabled: MFA_FEATURE_ENABLED,
      accessDeniedMessage,
      signIn,
      verifyTotp,
      cancelTotpChallenge,
      signOut,
      clearAccessDeniedMessage,
    }),
    [
      accessDeniedMessage,
      cancelTotpChallenge,
      clearAccessDeniedMessage,
      isAdmin,
      isLoading,
      requiresTotp,
      session,
      signIn,
      signOut,
      user,
      verifyTotp,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutDialog
        open={warningSeconds !== null}
        remainingSeconds={warningSeconds ?? 0}
        onContinue={() => {
          lastActivityRef.current = Date.now();
          setWarningSeconds(null);
        }}
        onLogout={() => {
          signOut().catch(() => undefined);
        }}
      />
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }

  return context;
}
